import axios from 'axios';
import { authHeader } from './auth-header';
import { showToast } from '../services/toast';
import { isTokenExpired, getToken } from './authService';
import { handleSessionEnd } from './common';
import {
    OFFLINE_ERROR_MESSAGE,
    NETWORK_ERROR_MESSAGE,
    isRetryableError,
    getRetryDelay,
    getMaxRetries,
} from './networkUtils';
import { addObservabilityInterceptors } from '../observability';

/**
 * Central axios instance with request auth and response error handling.
 * - Observability: x-app-name, x-device-id, traceparent; Faro reporting on 5xx/network/timeout (no toasts).
 * - Request: offline check; if token expired or user in storage but no token, session end and reject; else adds auth header.
 * - Response error: 401 → session end; retryable (5xx/408/network) → retry with backoff; then toasts for network/403/429/5xx.
 * - 401 and network errors set alreadyShownToast so callers do not show duplicate toasts.
 */
const axiosInstance = axios.create();

addObservabilityInterceptors(axiosInstance);

axiosInstance.interceptors.request.use(
    (config) => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            const offlineError = new Error(OFFLINE_ERROR_MESSAGE);
            offlineError.isOffline = true;
            return Promise.reject(offlineError);
        }
        if (isTokenExpired()) {
            handleSessionEnd('expired');
            const err = new Error('Session expired');
            err.alreadyShownToast = true;
            err.isSessionEnd = true;
            return Promise.reject(err);
        }

        if (localStorage.getItem('user') && !getToken()) {
            handleSessionEnd('expired');
            const err = new Error('Session expired');
            err.alreadyShownToast = true;
            err.isSessionEnd = true;
            return Promise.reject(err);
        }

        const headers = authHeader();
        const requestId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `req-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
        config.headers = {
            ...headers,
            'X-Request-ID': requestId,
            ...config.headers,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0'
        };
        return config;
    },
    (error) => Promise.reject(error)
);

function isRequestCanceled(error) {
    return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || axios.isCancel?.(error);
}

function isOnAuthPage() {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname || '';
    return (
        path === '/login' ||
        path.startsWith('/login/') ||
        path === '/forgotpassword' ||
        path === '/validateotp' ||
        path === '/reset-password'
    );
}

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (isRequestCanceled(error)) {
            return Promise.reject(error);
        }

        if (error.response?.status === 401) {
            // Login/auth failures also return 401 in some cases — do not treat as session end.
            if (!isOnAuthPage()) {
                handleSessionEnd('401');
                const err = new Error('Session expired');
                err.alreadyShownToast = true;
                err.isSessionEnd = true;
                return Promise.reject(err);
            }
            return Promise.reject(error);
        }

        const config = error.config || {};
        const retryCount = config.__retryCount ?? 0;
        if (isRetryableError(error) && retryCount < getMaxRetries()) {
            config.__retryCount = retryCount + 1;
            const delay = getRetryDelay(config.__retryCount);
            return new Promise((resolve) => setTimeout(resolve, delay)).then(() =>
                axiosInstance.request(config)
            );
        }

        if (error.isOffline) {
            showToast(OFFLINE_ERROR_MESSAGE, 'warn');
            const offlineErr = new Error(OFFLINE_ERROR_MESSAGE);
            offlineErr.alreadyShownToast = true;
            return Promise.reject(offlineErr);
        }
        if (!error.response) {
            showToast(NETWORK_ERROR_MESSAGE, 'error');
            const networkErr = new Error('Network error');
            networkErr.alreadyShownToast = true;
            return Promise.reject(networkErr);
        }

        const response = error.response;
        if (response.status === 403) {
            showToast('Access forbidden.', 'error');
            const err = new Error('Access forbidden');
            err.alreadyShownToast = true;
            return Promise.reject(err);
        }
        if (response.status === 429) {
            showToast('Too many attempts. Please wait a few minutes and try again.', 'error');
            const err = new Error('Too many attempts. Please wait a few minutes and try again.');
            err.alreadyShownToast = true;
            return Promise.reject(err);
        }
        if (response.status >= 500) {
            showToast('Server error. Please try again.', 'error');
            const err = new Error('Server error');
            err.alreadyShownToast = true;
            return Promise.reject(err);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;
